<script setup lang="ts">
import { integrationBaseUrl } from '@/modules/settings/services/webhooksApi'

const portal = new URL(integrationBaseUrl, window.location.origin).origin
const sections = [
  { id: 'contract', title: 'Контракт' },
  { id: 'signature', title: 'HMAC v1' },
  { id: 'incoming', title: 'Входящий API' },
  { id: 'outgoing', title: 'Исходящие события' },
  { id: 'errors', title: 'Ошибки и лимиты' },
]
const endpoints = [
  ['GET', 'employees/', 'Список сотрудников; search — поиск по ФИО'],
  ['GET', 'employees/{uuid}/', 'Данные одного сотрудника'],
  ['PATCH', 'employees/{uuid}/', 'Частичное изменение сотрудника'],
  ['GET', 'positions/', 'Справочник должностей: id, name'],
  ['GET', 'departments/', 'Активные отделения: id, name'],
]
const fields = [
  ['last_name', 'string', 'Фамилия, непустая строка'],
  ['first_name', 'string', 'Имя, непустая строка'],
  ['middle_name', 'string', 'Отчество; пустая строка удаляет значение'],
  ['position', 'UUID | null', 'ID существующей должности; null снимает назначение'],
  ['department', 'UUID | null', 'ID активного отделения; null снимает назначение'],
]
const headers = [
  ['X-Daomed-Key', 'UUID хука; публичный идентификатор'],
  ['X-Daomed-Timestamp', 'Целое число UNIX seconds; допустимое отклонение ±300 с'],
  ['X-Daomed-Nonce', 'Новый UUID на каждую HTTP-попытку'],
  ['X-Daomed-Idempotency-Key', 'UUID операции PATCH; у GET и исходящего POST заголовок отсутствует'],
  ['X-Daomed-Signature', 'v1= + HMAC-SHA256 в виде 64 строчных hex-цифр'],
]
const actions = [
  ['created', 'Создание карточки'],
  ['updated', 'Сохранение карточки'],
  ['deleted', 'Перемещение в архив'],
  ['restored', 'Восстановление из архива'],
  ['status_changed', 'Изменение статуса координаторской задачи'],
  ['coordinator_changed', 'Изменение координатора'],
]
const errors = [
  ['400', 'Недопустимое поле или значение, пустой PATCH, неверный offset либо повтор ключа идемпотентности с другим запросом'],
  ['401', 'Неверная подпись, время, nonce или IP; хук отключён, секрет заменён либо владелец утратил доступ'],
  ['403', 'Доступ отозван во время изменения; HMAC не авторизует обычные API портала'],
  ['404', 'Сотрудник не найден или находится в архиве'],
  ['429', 'Превышен лимит запросов; учитывайте заголовок Retry-After'],
  ['503', 'Хранилище секретов интеграций не настроено на портале'],
]
const canonical = `daomed-v1
{portal}
{webhook_uuid}
{timestamp_header}
{nonce_header}
{HTTP_METHOD_UPPERCASE}
{path_and_query}
{idempotency_key_or_empty_string}
{sha256_hex_of_raw_body}`
const requestExample = `import hashlib, hmac, json, os, time, uuid
from urllib.request import Request, HTTPRedirectHandler, ProxyHandler, build_opener

portal = os.environ["DAOMED_PORTAL"].rstrip("/")
key = os.environ["DAOMED_WEBHOOK_KEY"]  # UUID хука
secret = os.environ["DAOMED_WEBHOOK_SECRET"]  # строка секрета как показана в UI
path = "/api/integrations/v1/employees/" + os.environ["EMPLOYEE_UUID"] + "/"
body = json.dumps({"first_name": "Анна", "department": None},
                  ensure_ascii=False, separators=(",", ":")).encode("utf-8")
# Сохраните этот UUID и body для повторов одной операции.
idem = str(uuid.uuid4())

def signed_headers(method, path, body=b"", idem=""):
    ts, nonce = str(int(time.time())), str(uuid.uuid4())
    message = "\\n".join([
        "daomed-v1", portal, key, ts, nonce, method.upper(), path, idem,
        hashlib.sha256(body).hexdigest(),
    ]).encode("utf-8")
    headers = {
        "Content-Type": "application/json",
        "X-Daomed-Key": key,
        "X-Daomed-Timestamp": ts,
        "X-Daomed-Nonce": nonce,
        "X-Daomed-Signature": "v1=" + hmac.new(
            secret.encode("utf-8"), message, hashlib.sha256).hexdigest(),
    }
    if idem:
        headers["X-Daomed-Idempotency-Key"] = idem
    return headers

class NoRedirects(HTTPRedirectHandler):
    def redirect_request(self, *args, **kwargs):
        return None

opener = build_opener(ProxyHandler({}), NoRedirects())
request = Request(portal + path, data=body, method="PATCH",
                  headers=signed_headers("PATCH", path, body, idem))
with opener.open(request, timeout=15) as response:
    print(json.load(response))`
const getExample = `path = "/api/integrations/v1/employees/?offset=0&search=%D0%90%D0%BD%D0%BD%D0%B0"
request = Request(portal + path, headers=signed_headers("GET", path))
with opener.open(request, timeout=15) as response:
    print(json.load(response))`
const pageExample = `{
  "results": [{
    "id": "2381cc31-1439-463b-92e8-22a9341a227c",
    "last_name": "Иванова", "first_name": "Анна", "middle_name": "",
    "position": null, "department": null
  }],
  "next_offset": null
}`
const eventExample = JSON.stringify({
  schema_version: 1,
  event_id: '550e8400-e29b-41d4-a716-446655440000',
  event: 'ticket.status_changed',
  portal,
  occurred_at: '2026-10-25T09:00:00+00:00',
  data: {
    id: '123',
    number: 123,
    department_id: null,
    status: 'in_progress',
    coordinator_id: null,
    created_at: '2026-10-25T08:00:00+00:00',
    updated_at: '2026-10-25T08:00:00+00:00',
    is_deleted: false,
    url: `${portal}/tickets/123/`,
    category_id: null,
    service_id: '550e8400-e29b-41d4-a716-446655440002',
  },
  changes: { status: { before: 'new', after: 'in_progress' } },
}, null, 2)
const receiverExample = `# Псевдокод HTTP-обработчика. headers — регистронезависимое отображение.
# portal, key, secret берите из своей конфигурации, а не из тела события.
ts, nonce = headers["X-Daomed-Timestamp"], headers["X-Daomed-Nonce"]
if headers["X-Daomed-Key"] != key or abs(time.time() - int(ts)) > 300:
    return HTTP_401
uuid.UUID(nonce)
message = "\\n".join([
    "daomed-v1", portal, key, ts, nonce, "POST", request_path_and_query, "",
    hashlib.sha256(raw_body).hexdigest(),
]).encode("utf-8")
expected = "v1=" + hmac.new(secret.encode(), message, hashlib.sha256).hexdigest()
if not hmac.compare_digest(expected, headers["X-Daomed-Signature"]):
    return HTTP_401
# Атомарный INSERT UNIQUE (key, nonce), TTL >= 15 минут; повтор -> 401.
if not nonce_store.add_once(key, nonce):
    return HTTP_401
event = json.loads(raw_body)
# Проверить schema_version == 1 и event["portal"] == portal.
# В одной транзакции: дедупликация event_id + надёжная запись в свою очередь.
queue.enqueue_once(event["event_id"], event)
return HTTP_204`
function navigate(id: string) {
  document.getElementById(`webhook-docs-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<template>
  <div class="webhook-docs">
    <div class="mb-6">
      <div class="d-flex flex-wrap align-center ga-2 mb-2">
        <h2 class="text-h6">
          API интеграций
        </h2>
        <VChip size="small" variant="tonal">
          v1
        </VChip>
        <VChip size="small" variant="tonal">
          JSON · UTF-8
        </VChip>
      </div>
      <p class="text-body-2 text-medium-emphasis mb-3">
        Входящие запросы к порталу и исходящие HTTP-события. Общий протокол подписи HMAC-SHA256.
      </p>
      <div class="text-body-2">
        Портал: <code>{{ portal }}</code>
      </div>
      <div class="d-flex flex-wrap ga-2 mt-4" aria-label="Содержание справочника">
        <VBtn
          v-for="section in sections"
          :key="section.id"
          size="small"
          variant="outlined"
          @click="navigate(section.id)"
        >
          {{ section.title }}
        </VBtn>
      </div>
    </div>

    <section id="webhook-docs-contract">
      <h3>Контракт и доступ</h3>
      <ul>
        <li>Хук создаёт администратор портала. Входящий хук принимает запросы внешней системы; исходящий отправляет POST на её URL.</li>
        <li>Доступ ограничен текущим порталом: все сотрудники для кадрового API, все заявки и НС для событий. Дополнительных scope нет.</li>
        <li>Авторизация входящего API — только HMAC, без cookie, JWT и токена в URL. Секрет используется как UTF-8 строка, без Base64-декодирования.</li>
        <li>Каждый хук имеет отдельный секрет. Он доступен один раз при создании или ротации. Храните его в secret manager; ротация сразу отзывает прежний секрет.</li>
        <li>Все администраторы видят метаданные и журнал и могут отключить хук. Менять настройки, включать, ротировать секрет и повторять доставку может только владелец.</li>
        <li>Отключение хука, блокировка владельца или отзыв роли администратора останавливают обмен. Отменённые доставки автоматически не возобновляются.</li>
      </ul>
      <p>HTTPS обязателен. IP/CIDR-список необязателен: пустой допускает любой IP. До 32 записей IPv4/IPv6, сети не шире /24 и /64 соответственно. Для входящих запросов проверяется IP отправителя; для исходящих — IP сервера получателя.</p>
    </section>

    <section id="webhook-docs-signature">
      <h3>HMAC v1</h3>
      <VTable class="docs-table" density="compact">
        <thead><tr><th>Заголовок</th><th>Значение</th></tr></thead>
        <tbody>
          <tr v-for="row in headers" :key="row[0]">
            <td><code>{{ row[0] }}</code></td><td>{{ row[1] }}</td>
          </tr>
        </tbody>
      </VTable>
      <p>Каноническая строка: 9 строк UTF-8, разделитель — один LF (<code>\n</code>), без завершающего LF. Пустой ключ идемпотентности оставляет пустую восьмую строку.</p>
      <pre><code>{{ canonical }}</code></pre>
      <p><code>portal</code> — точный origin портала без завершающего <code>/</code>. <code>path_and_query</code> — исходный URL-encoded путь с ведущим <code>/</code> и query string в исходном порядке, без схемы и домена. У исходящего POST это путь URL получателя.</p>
      <p><code>signature = "v1=" + HMAC_SHA256(secret_utf8, canonical_utf8).hexdigest()</code>. SHA-256 вычисляйте от исходных байтов HTTP-тела; не пересобирайте JSON перед проверкой. Для GET тело пустое. Сравнение подписи — за постоянное время.</p>
    </section>

    <section id="webhook-docs-incoming">
      <h3>Входящий API · сотрудники</h3>
      <p>Базовый URL: <code>{{ integrationBaseUrl }}/v1/</code>. Завершающий <code>/</code> обязателен. Каждый запрос подписывается отдельно.</p>
      <VTable class="docs-table" density="compact">
        <thead><tr><th>Метод</th><th>Путь относительно v1/</th><th>Результат</th></tr></thead>
        <tbody>
          <tr v-for="row in endpoints" :key="row[0] + row[1]">
            <td><code>{{ row[0] }}</code></td><td><code>{{ row[1] }}</code></td><td>{{ row[2] }}</td>
          </tr>
        </tbody>
      </VTable>
      <p>Списки: <code>offset</code> от 0 до 1 000 000, страница до 100 записей. Следующий offset берите из <code>next_offset</code>; <code>null</code> завершает обход. Удалённые сотрудники исключены, заблокированные остаются доступны. Справочники возвращают только <code>id</code> и <code>name</code>.</p>
      <pre><code>{{ pageExample }}</code></pre>
      <h4>PATCH: разрешённые поля</h4>
      <VTable class="docs-table" density="compact">
        <thead><tr><th>Поле</th><th>Тип</th><th>Семантика</th></tr></thead>
        <tbody>
          <tr v-for="row in fields" :key="row[0]">
            <td><code>{{ row[0] }}</code></td><td><code>{{ row[1] }}</code></td><td>{{ row[2] }}</td>
          </tr>
        </tbody>
      </VTable>
      <p>Отсутствующее поле не меняется. Хотя бы одно разрешённое поле обязательно. Любое другое поле, включая телефон, email, роли и статус, отклоняет весь запрос с 400. Создания сотрудников и изменения справочников нет. Успех GET/PATCH — 200 с данными сотрудника.</p>
      <h4>Пример PATCH · Python 3, стандартная библиотека</h4>
      <p>Переменные окружения: <code>DAOMED_PORTAL</code> = <code>{{ portal }}</code>, <code>DAOMED_WEBHOOK_KEY</code>, <code>DAOMED_WEBHOOK_SECRET</code>, <code>EMPLOYEE_UUID</code>. Пример меняет имя и снимает назначение отделения.</p>
      <pre><code>{{ requestExample }}</code></pre>
      <h4>Пример GET · с функцией signed_headers выше</h4>
      <pre><code>{{ getExample }}</code></pre>
      <h4>Идемпотентность PATCH</h4>
      <p>При сетевой ошибке повторите те же метод, путь, байты тела и <code>X-Daomed-Idempotency-Key</code>, но с новыми timestamp, nonce и подписью. Сервер вернёт сохранённый ответ без повторного изменения. Другой запрос с тем же ключом — 400. Квитанция хранится 30 дней; каждой новой операции нужен новый UUID.</p>
    </section>

    <section id="webhook-docs-outgoing">
      <h3>Исходящие события · заявки и НС</h3>
      <p>Получатель — HTTPS URL на порту 443, без логина, пароля и фрагмента. Адрес должен разрешаться только в публичные IP; внутренние, loopback, link-local и служебные адреса запрещены даже с пустым IP-списком. Перенаправления не выполняются.</p>
      <p>Имена событий: <code>ticket.{action}</code> и <code>adverse.{action}</code> — 12 типов. В настройках выберите нужные подписки.</p>
      <VTable class="docs-table" density="compact">
        <thead><tr><th>action</th><th>Условие</th></tr></thead>
        <tbody>
          <tr v-for="row in actions" :key="row[0]">
            <td><code>{{ row[0] }}</code></td><td>{{ row[1] }}</td>
          </tr>
        </tbody>
      </VTable>
      <h4>Тело POST</h4>
      <pre><code>{{ eventExample }}</code></pre>
      <p><code>data.id</code> — строковое представление числового ID карточки, <code>number</code> — её номер (integer). <code>status</code>: <code>waiting</code>, <code>new</code>, <code>in_progress</code>, <code>completed</code>. Для НС вместо <code>category_id</code>/<code>service_id</code> передаётся <code>event_type_id</code>. Для смены координатора ключ в <code>changes</code> — <code>responsible_employee_id</code>. Без явного перехода <code>changes</code> равно <code>{}</code>. В событии только метаданные: описания, пациенты, контакты и файлы не передаются. Отдельного входящего API чтения заявок/НС нет.</p>
      <h4>Проверка получателем</h4>
      <pre><code>{{ receiverExample }}</code></pre>
      <p>Неверные/отсутствующие заголовки, некорректные UUID, JSON и неизвестную версию схемы обрабатывайте как отказ. Хранилище nonce и дедупликация должны быть общими для всех экземпляров получателя; локального set в памяти недостаточно.</p>
      <h4>Доставка и повторы</h4>
      <ul>
        <li>Событие фиксируется в транзакции изменения карточки; rollback не создаёт доставку. Исторические события новым подпискам не отправляются.</li>
        <li>Доставка асинхронная: проверка очереди каждые 15 с. Успех — любой 2xx. Иные ответы, включая 3xx, и сетевые ошибки вызывают повтор: до 10 попыток, интервалы от 30 с до 1 ч.</li>
        <li>Гарантия — at-least-once: дубликаты и перестановка событий возможны. При повторе <code>event_id</code> неизменен, timestamp/nonce/подпись новые. Дедуплицируйте по <code>event_id</code> и отвечайте 2xx только после надёжного сохранения.</li>
        <li><code>updated_at</code> относится к карточке, не является версией всего агрегата и может не меняться при изменении связанной задачи. Одно действие может породить несколько разных событий.</li>
        <li>Смена URL, IP-списка, подписок, секрета или отключение отменяют ожидающие доставки. Ошибочную доставку владелец может повторить из журнала; отменённую — нет.</li>
        <li>События фиксируются при модельном сохранении. Прямой SQL и bulk-операции их обходят; API не предоставляет CDC или строгую синхронизацию состояния.</li>
      </ul>
    </section>

    <section id="webhook-docs-errors">
      <h3>Ошибки и лимиты</h3>
      <VTable class="docs-table" density="compact">
        <thead><tr><th>HTTP</th><th>Входящий API</th></tr></thead>
        <tbody>
          <tr v-for="row in errors" :key="row[0]">
            <td><code>{{ row[0] }}</code></td><td>{{ row[1] }}</td>
          </tr>
        </tbody>
      </VTable>
      <p>До 120 запросов в минуту на хук. Тело до 16 KiB; превышение отклоняется при проверке запроса. Не повторяйте 400 без исправления запроса. Для 429 учитывайте <code>Retry-After</code>, для сетевых ошибок используйте backoff и идемпотентность.</p>
      <p>Квитанции PATCH и завершённые доставки хранятся 30 дней; nonce — не менее 15 минут до очистки. Журнал показывает метаданные и статусы, без секретов и тел обмена. Секреты и заголовки подписи не записывайте в логи своей системы.</p>
    </section>
  </div>
</template>

<style scoped>
.webhook-docs {
  max-inline-size: 1120px;
  line-height: 1.65;
  overflow-wrap: anywhere;
}
.webhook-docs section {
  padding-block: 24px;
  border-block-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  scroll-margin-block-start: 88px;
}
.webhook-docs h3 { margin-block-end: 16px; font-size: 1.125rem; }
.webhook-docs h4 { margin-block: 24px 12px; font-size: 0.9375rem; }
.webhook-docs p { margin-block: 16px; }
.webhook-docs ul { padding-inline-start: 22px; margin-block: 16px; }
.webhook-docs li + li { margin-block-start: 8px; }
.webhook-docs code { font-size: 0.8125rem; }
.webhook-docs pre {
  padding: 20px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgb(var(--v-theme-background));
  overflow-x: auto;
  white-space: pre;
  overflow-wrap: normal;
  line-height: 1.6;
  margin-block: 16px;
  max-inline-size: 100%;
}
.docs-table :deep(td), .docs-table :deep(th) { padding-block: 12px !important; vertical-align: top; }
.docs-table :deep(td:first-child) { white-space: nowrap; }
.docs-table :deep(table) { min-inline-size: 640px; }
@media (max-width: 600px) {
  .webhook-docs pre { padding: 12px; }
}
</style>
